import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
} from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import axiosClient from "../axiosClient";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axiosClient.get("api/user/profile/");
        setProfile(response.data);
      } catch (err) {
        setError("Failed to load profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Card elevation={24} sx={{ borderRadius: 4, p: 4 }}>
        {/* Top Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Avatar sx={{ bgcolor: deepPurple[500], width: 80, height: 80, mx: "auto", fontSize: 32 }}>
            {profile?.first_name?.charAt(0)}
          </Avatar>
          <Typography variant="h4" sx={{ mt: 2, fontWeight: "bold" }}>
            {profile.first_name} {profile.last_name}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {profile.user_role === 'ADMIN' ? "Secretary(Admin)" : profile.user_role === 'STAFF' ? "Doctor(STAFF)" : "Patient(Client)"}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Basic Info */}
        <SectionTitle title={t("Basic Information")} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Field label="Username" value={profile.username} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Email" value={profile.email} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Phone Number" value={profile.phone_number || "Not provided"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Date of Birth" value={profile.date_birth || "Not provided"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {console.log(profile.gender)}
            <Field label="Gender" value={formatGender(profile.gender) || "Not provided"} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Address Info */}
        <SectionTitle title={t("Address Information")} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Field label="City" value={profile.city || "Not provided"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Address" value={profile.address || "Not provided"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Postcode" value={profile.postcode || "Not provided"} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* GDPR Info */}
        <SectionTitle title={t("GDPR Information")} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Field label="GDPR Accepted" value={profile.gdpr_accepted ? "Yes" : "No"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="GDPR Agreement Date" value={profile.gdpr_agreement_date || "Not provided"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="GDPR IP Address" value={profile.gdpr_ip || "Not provided"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Hardcopy Consent" value={profile.hardcopy_consent ? "Yes" : "No"} />
          </Grid>
        </Grid>

        {/* Extra Details */}
        {(profile.department || profile.specializations || profile.gesy_number) && (
          <>
            <Divider sx={{ my: 3 }} />
            <SectionTitle title="Additional Information" />
            <Grid container spacing={2}>
              {profile.department && (
                <Grid item xs={12} sm={6}>
                  <Field label="Department" value={profile.department} />
                </Grid>
              )}
              {profile.specializations && (
                <Grid item xs={12} sm={6}>
                  <Field label="Specializations" value={profile.specializations} />
                </Grid>
              )}
              {profile.gesy_number && (
                <Grid item xs={12} sm={6}>
                  <Field label="GESY Number" value={profile.gesy_number} />
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Assigned Staff */}
        {profile.assigned_staff && Object.keys(profile.assigned_staff).length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <SectionTitle title="Assigned Staff" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field label="Name" value={profile.assigned_staff.name || "Not provided"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="Email" value={profile.assigned_staff.email || "Not provided"} />
              </Grid>
            </Grid>
          </>
        )}
      </Card>
    </Container>
  );
};

// Reusable Field component
const Field = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
);

// Reusable Section Title
const SectionTitle = ({ title }) => (
  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
    {title}
  </Typography>
);

// Gender formatter
const formatGender = (gender) => {
  if (gender === "M") return "Male";
  if (gender === "F") return "Female";
  return "Not provided";
};

export default Profile;
